import { z } from 'zod';
import { CalendarEventLink, CalendarEventLocationType, CalendarMeetingProvider, CalendarReminderMinutesBefore, } from './event.js';
// ---------------------------------------------------------------------------
// Custom-event create / update bodies
// ---------------------------------------------------------------------------
export const CreateCustomEventRequest = z.object({
    title: z.string().min(1).max(280),
    startsAt: z.union([
        z.string().datetime({ offset: true }),
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    ]),
    /** Optional end. Service rejects endsAt ≤ startsAt with
     *  CALENDAR_ENDS_BEFORE_STARTS. */
    endsAt: z.union([
        z.string().datetime({ offset: true }),
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    ]).nullable().optional(),
    allDay: z.boolean().optional(),
    /** RFC 5545 RRULE — accepted only when `calendar.recurrence` is on.
     *  Service rejects otherwise. */
    recurrence: z.string().min(1).max(500).nullable().optional(),
    /** Minutes before start to send the in-app reminder. Omit to use the
     *  server default; send null to disable reminders for this event. */
    reminderMinutesBefore: CalendarReminderMinutesBefore.nullable().optional(),
    /** Originating timezone — defaults to 'UTC' if unspecified. */
    tzid: z.string().min(1).max(64).optional(),
    /** Visibility scope. Empty array = visible only to the creator
     *  (private custom event). For shared-with-a-group events, attach
     *  one or more groupIds the creator belongs to. */
    groupIds: z.array(z.string().uuid()).max(64).optional(),
    /** Visual category — see `CalendarEvent.kind`. Required on the
     *  wire; the composer's segmented picker always has a non-empty
     *  selection. */
    kind: z.string().min(1).max(64),
    /** Free-form note — the composer's "Note / 备注" textarea writes
     *  this. Nullable; empty strings should be sent as null. */
    meta: z.string().min(1).max(2000).nullable().optional(),
    /** Cross-resource references — the composer's "Linked content /
     *  关联内容" picker writes this. Nullable / optional. */
    links: z.array(CalendarEventLink).max(16).nullable().optional(),
    /** Location and online meeting details. Send `locationType: null`
     *  (or omit all four keys on create) for no location. For
     *  `locationType: 'online'`, omitted provider defaults to Tencent
     *  Meeting server-side; `meetingDetail` can be a meeting number or a
     *  URL. */
    locationType: CalendarEventLocationType.nullable().optional(),
    meetingProvider: CalendarMeetingProvider.nullable().optional(),
    meetingDetail: z.string().min(1).max(1000).nullable().optional(),
    locationText: z.string().min(1).max(500).nullable().optional(),
});
export const UpdateCustomEventRequest = CreateCustomEventRequest.partial().extend({
    /** Controlled lifecycle toggle for calendar-side bulk/single edit.
     *  Only scheduled/cancelled are accepted here; live/past remain
     *  system-derived status transitions. */
    status: z.enum(['scheduled', 'cancelled']).optional(),
});
// ---------------------------------------------------------------------------
// Range query — the hot path. Drives the month/week/agenda views.
// ---------------------------------------------------------------------------
export const ListEventsQuery = z.object({
    /** Inclusive lower bound (UTC). */
    from: z.string().datetime({ offset: true }),
    /** Exclusive upper bound (UTC). Service rejects windows > 370
     *  days with CALENDAR_RANGE_TOO_LARGE — a year + slack is the
     *  largest UI surface in V1, larger windows are scripting/abuse. */
    to: z.string().datetime({ offset: true }),
    /** Filter by polymorphic subjectKind ('article' / 'custom' / ...).
     *  When omitted, returns events of every kind the viewer can see. */
    subjectKinds: z.array(z.string().min(1).max(64)).max(16).optional(),
    /** Filter by visual/event kind ('publish' / 'booking' / ...).
     *  This matches the calendar UI's type filter and is distinct from
     *  subjectKinds, which describes the owning source table. */
    kinds: z.array(z.string().min(1).max(64)).max(32).optional(),
    /** Restrict to events visible to one of these group ids. Defaults
     *  to "any group the viewer belongs to" (the route layer fills in
     *  viewer.groupIds for the empty-default case). */
    groupIds: z.array(z.string().uuid()).max(64).optional(),
    /** Filter by status. Defaults to ['scheduled', 'live'] — the
     *  forward-looking surface; clients wanting tombstones pass
     *  ['cancelled'] explicitly. */
    status: z
        .array(z.enum(['scheduled', 'live', 'past', 'cancelled']))
        .max(4)
        .optional(),
    /** Human-readable export timezone. Calendar list JSON remains UTC,
     *  but XLSX/TeX/PDF exporters project timed rows into this IANA zone. */
    timeZone: z.string().min(1).max(64).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
    offset: z.coerce.number().int().min(0).max(10_000).optional(),
});
//# sourceMappingURL=requests.js.map